/**
 * Shift Digital - Digital Ad to Lead Attribution (v2.1)
 * ============================================================================
 *
 * Captures the three pieces Shift Digital's "Digital Ad to Lead Attribution"
 * spec requires and stores them in a first-party cookie so the backend can
 * include them in the ADF/XML lead post when a visitor submits a form:
 *
 *   1. SD Session ID  -> sd('getSessionId', cb), refreshed on every page view.
 *   2. Query String Params -> every sd_* / utm_* param from the ENTRY URL of
 *      the session, retained for the whole session.
 *   3. Referrer -> document.referrer captured on the first page view.
 *
 * Transport: cookie `sd_lead_attribution` (URL-encoded JSON), read server-side
 * from $_COOKIE when the ADF lead is built. Shift's own spec explicitly allows
 * "the provider can choose to set a cookie" for this Session ID.
 *
 * Cookie shape:
 *   { "sdSessionId": "...", "referrer": "...", "qsp": { "sd_campaign": "...", ... } }
 *
 * Only enqueued for Stellantis dealers with Shift Digital tagging enabled
 * (see app/filters.php), so the `sd` global from the SD Analytics bootstrap in
 * partials/head.blade.php is always present when this runs.
 */
(function () {
    'use strict';

    var COOKIE_NAME = 'sd_lead_attribution';

    // ------------------------------------------------------------------
    // Cookie helpers
    // ------------------------------------------------------------------
    function readCookie() {
        var match = document.cookie.match(/(?:^|;\s*)sd_lead_attribution=([^;]*)/);
        if (!match) {
            return null;
        }
        try {
            return JSON.parse(decodeURIComponent(match[1]));
        } catch (e) {
            return null;
        }
    }

    function writeCookie(data) {
        var value = encodeURIComponent(JSON.stringify(data));
        var cookie = COOKIE_NAME + '=' + value + '; path=/; SameSite=Lax';
        // Only add Secure on https so it keeps working on http (local/staging).
        if (location.protocol === 'https:') {
            cookie += '; Secure';
        }
        document.cookie = cookie;
    }

    // ------------------------------------------------------------------
    // Query string capture — only sd_* and utm_* keys from the entry URL.
    // ------------------------------------------------------------------
    function captureQueryParams() {
        var qsp = {};
        var params = new URLSearchParams(location.search);
        params.forEach(function (value, key) {
            if (key.indexOf('sd_') === 0 || key.indexOf('utm_') === 0) {
                qsp[key] = value;
            }
        });
        return qsp;
    }

    // ------------------------------------------------------------------
    // Resolve the session's attribution data.
    //
    // The cookie doubles as the "session already started" marker (it is a
    // session cookie, so it dies with the browser session, matching the QSP
    // retention window):
    //   - No cookie  -> first page view: capture QSP + referrer (even if empty).
    //   - Cookie set -> mid-session: keep QSP + referrer across internal
    //     navigation, but refresh them if the URL carries fresh sd_/utm_
    //     params (a new ad click within the same session wins).
    // ------------------------------------------------------------------
    var existing = readCookie();
    var urlQsp = captureQueryParams();
    var hasUrlParams = Object.keys(urlQsp).length > 0;

    var data;
    if (!existing) {
        data = {
            sdSessionId: '',
            referrer: document.referrer || '',
            qsp: urlQsp
        };
    } else {
        data = existing;
        if (hasUrlParams) {
            data.qsp = urlQsp;
            data.referrer = document.referrer || '';
        }
    }

    // Persist QSP + referrer right away, before sd.js finishes loading, so the
    // data survives even if getSessionId never resolves on this page view.
    writeCookie(data);

    // ------------------------------------------------------------------
    // Refresh the SD Session ID on every page view. `sd` is the queueing stub
    // from the SD Analytics bootstrap, so the callback fires once sd.js loads.
    // ------------------------------------------------------------------
    if (typeof window.sd === 'function') {
        window.sd('getSessionId', function (sessionId) {
            if (sessionId) {
                data.sdSessionId = sessionId;
                writeCookie(data);
            }
        });
    }
})();
